import { getAuthToken, getLastAuthError } from './authToken';

const REQUEST_TIMEOUT_MS = 15000;

export async function fetchWithAuth(input: RequestInfo | URL, init: RequestInit = {}) {
  const token = await getAuthToken();
  if (!token) {
    const authError = getLastAuthError();
    throw new Error(
      authError
        ? `Missing authentication token. ${authError}`
        : 'Missing authentication token. Please sign in again.',
    );
  }

  const headers = new Headers(init.headers ?? undefined);
  headers.set('Authorization', `Bearer ${token}`);

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  const forwardAbort = () => controller.abort();

  if (init.signal) {
    if (init.signal.aborted) {
      controller.abort();
    } else {
      init.signal.addEventListener('abort', forwardAbort, { once: true });
    }
  }

  try {
    return await fetch(input, {
      ...init,
      headers,
      mode: 'cors',
      credentials: 'include',
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s. Please try again.`);
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
    if (init.signal) {
      init.signal.removeEventListener('abort', forwardAbort);
    }
  }
}
